// use-hl-positions — Scan über Top-N-Wallets aus dem Leaderboard.
// Lädt für jede Wallet `clearinghouseState`, filtert auf Positionen mit
// dem gewünschten coin, mapped auf die UI-Row-Struktur.
//
// Batch-parallel via scanClearinghouseStates (default 8 parallel pro Welle).
// Re-scan bei Coin-Wechsel + manuell via refetch(). Triggered NUR durch
// triggerScan()/refetch() — kein Auto-Mount (Scan kostet ~N/8 × Latenz).

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  eveScore,
  scanClearinghouseStates,
  type LeaderboardRow,
} from '@/lib/hl-info-api';
import type { WalletQuality } from '@/lib/hl-quality';
import { getQuality } from '@/lib/hl-quality';
import type { HlPositionRow } from '@/components/hyperliquid/positions-tab';

export interface UseHlPositionsOptions {
  /** Anzahl Top-Wallets die skannt werden (default 50). */
  topN?: number;
  /** Batch-Größe (parallel pro Welle, default 8). */
  batchSize?: number;
  /** Auto-Scan beim Mount + bei coin-Wechsel (default false — User triggert). */
  autoScan?: boolean;
}

export interface UseHlPositionsReturn {
  rows: HlPositionRow[];
  loading: boolean;
  /** Wie viele Wallets schon skannt. */
  scanned: number;
  total: number;
  /** Trigger manuellen Scan. */
  triggerScan: () => Promise<void>;
}

export function useHlPositions(
  coin: string,
  leaderboard: ReadonlyArray<LeaderboardRow>,
  livePrices: Record<string, string>,
  options: UseHlPositionsOptions = {},
): UseHlPositionsReturn {
  const { topN = 50, batchSize = 8, autoScan = false } = options;
  const [rows, setRows] = useState<HlPositionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [scanned, setScanned] = useState(0);
  const [total, setTotal] = useState(0);

  const lbRef = useRef(leaderboard);
  const pxRef = useRef(livePrices);
  useEffect(() => {
    lbRef.current = leaderboard;
  }, [leaderboard]);
  useEffect(() => {
    pxRef.current = livePrices;
  }, [livePrices]);

  const triggerScan = useCallback(async (): Promise<void> => {
    if (!coin) return;
    const sorted = [...lbRef.current]
      .sort((a, b) => b.accountValue - a.accountValue)
      .slice(0, topN);
    if (sorted.length === 0) return;

    setLoading(true);
    setRows([]);
    setScanned(0);
    setTotal(sorted.length);

    const addresses = sorted.map((w) => w.ethAddress);
    const stateMap = await scanClearinghouseStates(addresses, batchSize, (s) =>
      setScanned(s),
    );

    const result: HlPositionRow[] = [];
    for (const wallet of sorted) {
      const state = stateMap.get(wallet.ethAddress);
      if (!state) continue;
      const matching = (state.assetPositions ?? []).filter(
        (ap) => ap.position?.coin === coin,
      );
      for (const ap of matching) {
        const pos = ap.position;
        const size = num(pos.szi);
        if (size === 0) continue;
        const entry = num(pos.entryPx);
        const livePx = num(pxRef.current[coin]);
        const mark = livePx > 0 ? livePx : entry;
        const valueUsd = num(pos.positionValue) || Math.abs(size) * mark;
        const pnlAbs = num(pos.unrealizedPnl);
        const pnlPct = valueUsd > 0 ? (pnlAbs / valueUsd) * 100 : 0;
        const leverage =
          typeof pos.leverage?.value === 'number' ? pos.leverage.value : 1;

        const vol = wallet.perf.allTime.vlm;
        const pnl = wallet.perf.allTime.pnl;
        const quality: WalletQuality = getQuality({
          av: wallet.accountValue,
          vol,
          pnl,
        });

        result.push({
          id: `${wallet.ethAddress}-${pos.coin}`,
          label: wallet.displayName ?? undefined,
          address: wallet.ethAddress,
          sizeBtc: size,
          entry,
          mark,
          valueUsd,
          pnlPct,
          leverage,
          quality,
        });
      }
    }

    // Sortieren nach Value (größte Positionen oben).
    result.sort((a, b) => b.valueUsd - a.valueUsd);
    setRows(result);
    setLoading(false);
  }, [coin, topN, batchSize]);

  useEffect(() => {
    if (!autoScan) return;
    void triggerScan();
  }, [autoScan, triggerScan]);

  // Eve-Score-Sort: optionaler Convenience-Hook für Caller wenn sie
  // Top-EVE-Score-Wallets wollen — z.B. um die Leaderboard-Top-N anders zu
  // priorisieren.
  // (Hier inline statt eigener Hook weil der Use-Case oft mit Position-Scan
  // gepaart ist.)
  return { rows, loading, scanned, total, triggerScan };
}

function num(v: unknown): number {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (typeof v !== 'string') return 0;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

export { eveScore };
